import React, { useState, useEffect } from 'react';
import { Package, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { getGlassStyles, getButtonStyles } from './ProductManagementStyles';
import { quantityStep, sanitizeQuantity } from 'core/utils/units';
import MultilingualTextEditor from './MultilingualTextEditor';
import StockLocationsEditor from './StockLocationsEditor';
import SourcesEditor from './SourcesEditor';
import AttributesEditor from './AttributesEditor';
import JsonTableEditor from './JsonTableEditor';
import JsonObjectEditor from './JsonObjectEditor';

const ProductForm = ({ product, shop, onSave, onCancel, existingCategories, showWarning }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'EUR',
    unit: 'STK',
    category: '',
    sku: '',
    vendor_mat: '',
    preferred_vendor: '',
    fixed_vendor: '',
    image_url: '',
    is_active: true,
    stock_quantity: '',
    min_order_quantity: 1.00,
    cost_center: '',
    purchasing_org: '',
    plant: '',
    price_source: '',
    manufacturer: '',
    manufacturer_part_no: '',
    purchasing_group: '',
    planned_delivery_time: '',
    technical_attributes: '',
    price_scales: '',
    contract_data: '',
    documents: '',
    compliance: '',
    // Neue SAP-Felder
    source: 'internal',
    ean: '',
    price_unit: 1,
    material_type: '',
    status: 'active',
    gross_weight: '',
    net_weight: '',
    weight_unit: '',
    size_text: '',
    profit_center: '',
    // Arrays
    texts: [{ lang: 'de', name: '', description: '' }],
    stock: [],
    sources: [],
    attributes: [],
  });

  const styles = getGlassStyles(isDarkMode);
  const primaryButtonStyles = getButtonStyles(isDarkMode, 'primary');
  const secondaryButtonStyles = getButtonStyles(isDarkMode, 'secondary');

  useEffect(() => {
    if (product) {
      // texts[]-Array: wenn leer, DE-Fallback aus name/description vorbelegen
      const rawTexts = Array.isArray(product.texts) && product.texts.length > 0
        ? product.texts
        : [{ lang: 'de', name: product.name || '', description: product.description || '' }];

      const rawStock = Array.isArray(product.stock) ? product.stock : [];
      const rawSources = Array.isArray(product.sources) ? product.sources : [];
      const rawAttributes = Array.isArray(product.attributes) ? product.attributes : [];

      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        currency: product.currency || 'EUR',
        unit: product.unit || 'STK',
        category: product.category || '',
        sku: product.sku || '',
        vendor_mat: product.vendor_mat || '',
        preferred_vendor: product.preferred_vendor || '',
        fixed_vendor: product.fixed_vendor || '',
        image_url: product.image_url || '',
        is_active: product.is_active !== undefined ? product.is_active : true,
        stock_quantity: product.stock_quantity || '',
        min_order_quantity: product.min_order_quantity || 1.00,
        cost_center: product.cost_center || '',
        purchasing_org: product.purchasing_org || '',
        plant: product.plant || '',
        price_source: product.price_source || '',
        manufacturer: product.manufacturer || '',
        manufacturer_part_no: product.manufacturer_part_no || '',
        purchasing_group: product.purchasing_group || '',
        planned_delivery_time: product.planned_delivery_time || '',
        technical_attributes: product.technical_attributes && typeof product.technical_attributes === 'object' ? JSON.stringify(product.technical_attributes, null, 2) : (product.technical_attributes || ''),
        price_scales: product.price_scales && typeof product.price_scales === 'object' ? JSON.stringify(product.price_scales, null, 2) : (product.price_scales || ''),
        contract_data: product.contract_data && typeof product.contract_data === 'object' ? JSON.stringify(product.contract_data, null, 2) : (product.contract_data || ''),
        documents: product.documents && typeof product.documents === 'object' ? JSON.stringify(product.documents, null, 2) : (product.documents || ''),
        compliance: product.compliance && typeof product.compliance === 'object' ? JSON.stringify(product.compliance, null, 2) : (product.compliance || ''),
        // Neue SAP-Felder
        source: product.source || 'internal',
        ean: product.ean || '',
        price_unit: product.price_unit || 1,
        material_type: product.material_type || '',
        status: product.status || 'active',
        gross_weight: product.gross_weight || '',
        net_weight: product.net_weight || '',
        weight_unit: product.weight_unit || '',
        size_text: product.size_text || '',
        profit_center: product.profit_center || '',
        texts: rawTexts,
        stock: rawStock,
        sources: rawSources,
        attributes: rawAttributes,
      });
    }
  }, [product]);

  const isSapSource = formData.source === 'sap';

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      if (showWarning) {
        showWarning(t('admin.productForm.nameRequired'));
      } else {
        alert(t('admin.productForm.nameRequired'));
      }
      return;
    }
    
    if (!formData.price || parseFloat(formData.price) < 0) {
      if (showWarning) {
        showWarning(t('admin.productForm.priceRequired'));
      } else {
        alert(t('admin.productForm.priceRequired'));
      }
      return;
    }

    const submitData = {
      ...formData,
      price: parseFloat(formData.price),
      min_order_quantity: parseFloat(formData.min_order_quantity),
      stock_quantity: formData.stock_quantity ? parseInt(formData.stock_quantity) : null,
      price_unit: parseInt(formData.price_unit) || 1,
      gross_weight: formData.gross_weight !== '' ? parseFloat(formData.gross_weight) : null,
      net_weight: formData.net_weight !== '' ? parseFloat(formData.net_weight) : null,
      // JSON-Felder werden als String uebertragen — kein parse, Backend speichert String
      technical_attributes: formData.technical_attributes,
      price_scales: formData.price_scales,
      contract_data: formData.contract_data,
      documents: formData.documents,
      compliance: formData.compliance,
      // Arrays direkt als Objekte senden
      texts: formData.texts,
      stock: formData.stock,
      sources: formData.sources,
      attributes: formData.attributes,
    };

    onSave(submitData);
  };

  const renderInputField = (label, type, value, onChange, required = false, placeholder = '', props = {}) => (
    <div className="relative group/input">
      <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
        {label} {required && '*'}
      </label>
      <div className="relative">
        <div className={styles.inputGlassBackground}></div>
        <input
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          className={styles.inputClassName}
          {...props}
        />
        <div className={styles.focusHighlight}></div>
      </div>
    </div>
  );

  const renderSelectField = (label, value, onChange, options, required = false) => (
    <div className="relative group/select">
      <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
        {label} {required && '*'}
      </label>
      <div className="relative">
        <div className={styles.inputGlassBackground}></div>
        <select
          value={value}
          onChange={onChange}
          required={required}
          className={`${styles.inputClassName} appearance-none cursor-pointer`}
        >
          {options.map(option => (
            <option key={option.value} value={option.value} className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none z-10">
          <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <div className={styles.focusHighlight}></div>
      </div>
    </div>
  );

  const renderTextareaField = (label, value, onChange, rows = 3) => (
    <div className="relative group/input">
      <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
        {label}
      </label>
      <div className="relative">
        <div className={styles.inputGlassBackground}></div>
        <textarea
          value={value}
          onChange={onChange}
          rows={rows}
          className={`${styles.inputClassName} resize-none`}
        />
        <div className={styles.focusHighlight}></div>
      </div>
    </div>
  );

  return (
    <div className={`${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black' : 'bg-white'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="relative group">
          <div className={styles.glassBackground}></div>
          <div className={styles.glassOverlay}></div>
          
          <div className="relative p-8">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center space-x-3">
                <div className="relative group/icon">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm"></div>
                  <div className="relative bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl w-12 h-12 rounded-2xl flex items-center justify-center shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20">
                    <Package className="w-6 h-6 text-white/90 drop-shadow-lg" />
                    <div className="absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm"></div>
                  </div>
                </div>
                <div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent' : 'text-slate-900'}`}>
                    {product ? t('admin.productForm.editTitle') : t('admin.productForm.createTitle')}
                  </h3>
                  <p className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{shop.name} ({t('admin.productForm.internalShop')})</p>
                </div>
              </div>
              
              <button
                onClick={onCancel}
                className={secondaryButtonStyles.className}
                title={t('common.close')}
                style={secondaryButtonStyles.style}
              >
                <div 
                  className={secondaryButtonStyles.hoverOverlay}
                  style={secondaryButtonStyles.hoverStyle}
                ></div>
                
                <div className={`relative z-10 p-2 flex items-center justify-center transition-all duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                } drop-shadow-lg group-hover:drop-shadow-xl`}>
                  <ArrowLeft className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
                </div>
                
                <div className={secondaryButtonStyles.waveEffect}>
                  <div 
                    className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-800"
                    style={{
                      background: isDarkMode
                        ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                        : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)'
                    }}
                  ></div>
                </div>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">

              {/* SAP Read-only Banner — bleibt oben */}
              {isSapSource && (
                <div className={`flex items-start gap-3 rounded-2xl p-4 ${
                  isDarkMode
                    ? 'bg-amber-500/10 border border-amber-400/30'
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                    {t('admin.productForm.sapReadOnlyHint')}
                  </p>
                </div>
              )}

              {/* ===== (1) Grunddaten ===== */}
              <div className="flex items-center gap-3">
                <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                <span className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'}`}>
                  {t('admin.productForm.baseSection')}
                </span>
                <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderInputField(
                  t('admin.productForm.name'),
                  'text',
                  formData.name,
                  (e) => setFormData({...formData, name: e.target.value}),
                  true
                )}

                {renderInputField(
                  t('admin.productForm.sku'),
                  'text',
                  formData.sku,
                  (e) => setFormData({...formData, sku: e.target.value})
                )}
              </div>

              {renderTextareaField(
                t('admin.productForm.description'),
                formData.description,
                (e) => setFormData({...formData, description: e.target.value})
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {renderInputField(
                  t('admin.productForm.price'),
                  'number',
                  formData.price,
                  (e) => setFormData({...formData, price: e.target.value}),
                  true,
                  '',
                  { step: '0.01', min: '0' }
                )}

                {renderSelectField(
                  t('admin.productForm.currency'),
                  formData.currency,
                  (e) => setFormData({...formData, currency: e.target.value}),
                  [
                    { value: 'EUR', label: 'EUR' },
                    { value: 'USD', label: 'USD' },
                    { value: 'CHF', label: 'CHF' }
                  ]
                )}

                {renderInputField(
                  t('admin.productForm.unit'),
                  'text',
                  formData.unit,
                  (e) => setFormData({...formData, unit: e.target.value}),
                  false,
                  'STK, KG, m, etc.'
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative group/input">
                  <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
                    {t('admin.productForm.productGroup')}
                  </label>
                  <div className="relative">
                    <div className={styles.inputGlassBackground}></div>
                    <input
                      type="text"
                      list="categories"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      placeholder={t('admin.productForm.productGroupPlaceholder')}
                      className={styles.inputClassName}
                    />
                    <datalist id="categories">
                      {existingCategories.map(category => (
                        <option key={category} value={category} />
                      ))}
                    </datalist>
                    <div className={styles.focusHighlight}></div>
                  </div>
                </div>

                {renderInputField(
                  t('admin.productForm.supplierArtNo'),
                  'text',
                  formData.vendor_mat,
                  (e) => setFormData({...formData, vendor_mat: e.target.value})
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderInputField(
                  t('admin.productForm.preferredVendor'),
                  'text',
                  formData.preferred_vendor,
                  (e) => setFormData({...formData, preferred_vendor: e.target.value})
                )}

                {renderInputField(
                  t('admin.productForm.fixedVendor'),
                  'text',
                  formData.fixed_vendor,
                  (e) => setFormData({...formData, fixed_vendor: e.target.value})
                )}
              </div>

              {renderInputField(
                t('admin.productForm.imageUrl'),
                'url',
                formData.image_url,
                (e) => setFormData({...formData, image_url: e.target.value}),
                false,
                'https://example.com/product-image.jpg'
              )}
              {/* ===== Ende Grunddaten ===== */}

              {/* ===== (2) Beschaffung / Kontierung ===== */}
              <div className="pt-2">
                <div className={`flex items-center gap-3 mb-6`}>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                  <span className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'}`}>
                    {t('admin.productForm.detailsSection')}
                  </span>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                </div>

                {/* Kostenstelle, Einkaufsorganisation, Werk */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {renderInputField(
                    t('admin.productForm.costCenter'),
                    'text',
                    formData.cost_center,
                    (e) => setFormData({...formData, cost_center: e.target.value}),
                    false,
                    'z.B. 110700'
                  )}

                  {renderInputField(
                    t('admin.productForm.purchasingOrg'),
                    'text',
                    formData.purchasing_org,
                    (e) => setFormData({...formData, purchasing_org: e.target.value}),
                    false,
                    'z.B. 1000'
                  )}

                  {renderInputField(
                    t('admin.productForm.plant'),
                    'text',
                    formData.plant,
                    (e) => setFormData({...formData, plant: e.target.value}),
                    false,
                    'z.B. 1000'
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Lagerbestand - disabled bis SAP Schnittstelle */}
                  <div className="relative group/input">
                    <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white/50' : 'text-gray-400'} mb-3 drop-shadow-lg`}>
                      {t('admin.productForm.stock')}
                      <span className={`ml-2 text-xs ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>
                        {t('admin.productForm.stockSapPending')}
                      </span>
                    </label>
                    <div className="relative">
                      <div className={styles.inputGlassBackground} style={{ opacity: 0.5 }}></div>
                      <input
                        type="number"
                        value={formData.stock_quantity}
                        onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                        disabled
                        placeholder={t('admin.productForm.stockSapSync')}
                        className={`${styles.inputClassName} cursor-not-allowed opacity-50`}
                        min="0"
                      />
                      <div className={styles.focusHighlight}></div>
                    </div>
                  </div>

                  {renderInputField(
                    t('admin.productForm.minOrderQuantity'),
                    'number',
                    formData.min_order_quantity,
                    (e) => setFormData({...formData, min_order_quantity: sanitizeQuantity(e.target.value, formData.unit)}),
                    false,
                    '',
                    { step: quantityStep(formData.unit), min: quantityStep(formData.unit) }
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {renderInputField(
                    t('admin.productForm.manufacturer'),
                    'text',
                    formData.manufacturer,
                    (e) => setFormData({...formData, manufacturer: e.target.value})
                  )}
                  {renderInputField(
                    t('admin.productForm.manufacturerPartNo'),
                    'text',
                    formData.manufacturer_part_no,
                    (e) => setFormData({...formData, manufacturer_part_no: e.target.value})
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {renderInputField(
                    t('admin.productForm.purchasingGroup'),
                    'text',
                    formData.purchasing_group,
                    (e) => setFormData({...formData, purchasing_group: e.target.value}),
                    false,
                    'z.B. 010'
                  )}
                  {renderInputField(
                    t('admin.productForm.plannedDeliveryTime'),
                    'text',
                    formData.planned_delivery_time,
                    (e) => setFormData({...formData, planned_delivery_time: e.target.value}),
                    false,
                    'z.B. 5 Werktage'
                  )}
                </div>

                {/* price_source Select */}
                <div className="mb-6">
                  {renderSelectField(
                    t('admin.productForm.priceSource'),
                    formData.price_source,
                    (e) => setFormData({...formData, price_source: e.target.value}),
                    [
                      { value: '', label: `– ${t('admin.productForm.priceSourceNone')} –` },
                      { value: 'contract', label: t('admin.productForm.priceSourceContract') },
                      { value: 'inforecord_plant', label: t('admin.productForm.priceSourceInforecordPlant') },
                      { value: 'inforecord_general', label: t('admin.productForm.priceSourceInforecordGeneral') },
                      { value: 'estimate', label: t('admin.productForm.priceSourceEstimate') },
                      { value: 'internal', label: t('admin.productForm.priceSourceInternal') }
                    ]
                  )}
                </div>
              </div>
              {/* ===== Ende Beschaffung / Kontierung ===== */}

              {/* ===== (3) SAP-Identifikation ===== */}
              <div className="pt-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                  <span className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'}`}>
                    {t('admin.productForm.sapSection')}
                  </span>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {renderInputField(
                    t('admin.productForm.ean'),
                    'text',
                    formData.ean,
                    (e) => setFormData({...formData, ean: e.target.value}),
                    false,
                    '4012345678901',
                    { disabled: isSapSource }
                  )}

                  {renderInputField(
                    t('admin.productForm.priceUnit'),
                    'number',
                    formData.price_unit,
                    (e) => setFormData({...formData, price_unit: e.target.value}),
                    false,
                    '1',
                    { step: '1', min: '1', disabled: isSapSource }
                  )}

                  {renderSelectField(
                    t('admin.productForm.materialType'),
                    formData.material_type,
                    (e) => setFormData({...formData, material_type: e.target.value}),
                    [
                      { value: '', label: `– ${t('admin.productForm.materialTypeNone')} –` },
                      { value: 'ROH', label: 'ROH – Rohstoffe' },
                      { value: 'HALB', label: 'HALB – Halbfabrikate' },
                      { value: 'HAWA', label: 'HAWA – Handelswaren' },
                      { value: 'DIEN', label: 'DIEN – Dienstleistungen' },
                      { value: 'UNBW', label: 'UNBW – Unbewertete Materialien' },
                    ]
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {renderSelectField(
                    t('admin.productForm.materialStatus'),
                    formData.status,
                    (e) => setFormData({...formData, status: e.target.value}),
                    [
                      { value: 'active', label: t('admin.productForm.statusActive') },
                      { value: 'blocked', label: t('admin.productForm.statusBlocked') },
                      { value: 'discontinued', label: t('admin.productForm.statusDiscontinued') },
                    ]
                  )}

                  {renderInputField(
                    t('admin.productForm.profitCenter'),
                    'text',
                    formData.profit_center,
                    (e) => setFormData({...formData, profit_center: e.target.value}),
                    false,
                    'z.B. 1100',
                    { disabled: isSapSource }
                  )}

                  {renderInputField(
                    t('admin.productForm.sizeText'),
                    'text',
                    formData.size_text,
                    (e) => setFormData({...formData, size_text: e.target.value}),
                    false,
                    'z.B. DN50 PN16',
                    { disabled: isSapSource }
                  )}
                </div>

                {/* Gewicht */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {renderInputField(
                    t('admin.productForm.grossWeight'),
                    'number',
                    formData.gross_weight,
                    (e) => setFormData({...formData, gross_weight: e.target.value}),
                    false,
                    '0.000',
                    { step: '0.001', min: '0', disabled: isSapSource }
                  )}
                  {renderInputField(
                    t('admin.productForm.netWeight'),
                    'number',
                    formData.net_weight,
                    (e) => setFormData({...formData, net_weight: e.target.value}),
                    false,
                    '0.000',
                    { step: '0.001', min: '0', disabled: isSapSource }
                  )}
                  {renderInputField(
                    t('admin.productForm.weightUnit'),
                    'text',
                    formData.weight_unit,
                    (e) => setFormData({...formData, weight_unit: e.target.value}),
                    false,
                    'KG, G, LB',
                    { disabled: isSapSource }
                  )}
                </div>
              </div>
              {/* ===== Ende SAP-Identifikation ===== */}

              {/* ===== (4) Mehrsprach-Editor ===== */}
              <div className="pt-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                  <span className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'}`}>
                    {t('admin.productForm.textsSection')}
                  </span>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                </div>
                <MultilingualTextEditor
                  texts={formData.texts}
                  onTextsChange={(newTexts) => setFormData({...formData, texts: newTexts})}
                  disabled={isSapSource}
                />
              </div>
              {/* ===== Ende Mehrsprach-Editor ===== */}

              {/* ===== (5) Multi-Lagerort-Editor ===== */}
              <div className="pt-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                  <span className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'}`}>
                    {t('admin.productForm.stockSection')}
                  </span>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                </div>
                <StockLocationsEditor
                  stock={formData.stock}
                  onStockChange={(newStock) => setFormData({...formData, stock: newStock})}
                  disabled={isSapSource}
                />
              </div>
              {/* ===== Ende Multi-Lagerort-Editor ===== */}

              {/* ===== (6) Bezugsquellen-Editor ===== */}
              <div className="pt-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                  <span className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'}`}>
                    {t('admin.productForm.sourcesSection')}
                  </span>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                </div>
                <SourcesEditor
                  sources={formData.sources}
                  onSourcesChange={(newSources) => setFormData({...formData, sources: newSources})}
                  disabled={isSapSource}
                />
              </div>
              {/* ===== Ende Bezugsquellen-Editor ===== */}

              {/* ===== (7) Technische Merkmale (strukturiert) ===== */}
              <div className="pt-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                  <span className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'}`}>
                    {t('admin.productForm.attributes.title')}
                  </span>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                </div>
                <AttributesEditor
                  attributes={formData.attributes}
                  onAttributesChange={(newAttrs) => setFormData({...formData, attributes: newAttrs})}
                  disabled={isSapSource}
                />
              </div>
              {/* ===== Ende Technische Merkmale ===== */}

              {/* ===== (8) JSON-Detail-Felder ===== */}
              <div className="pt-2">
                <div className={`flex items-center gap-3 mb-6`}>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                  <span className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'} opacity-60`}>
                    JSON
                  </span>
                  <div className={`h-px flex-1 ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
                </div>

                {/* JSON-Felder als Textareas */}
                <div className="space-y-6">
                  <div className="relative group/input">
                    <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
                      {t('admin.productForm.technicalAttributes')}
                    </label>
                    <JsonTableEditor
                      value={formData.technical_attributes}
                      onChange={(s) => setFormData({ ...formData, technical_attributes: s })}
                      columns={[
                        { key: 'label', label: t('admin.jsonEditor.attrLabel'), placeholder: 'Spannung' },
                        { key: 'value', label: t('admin.jsonEditor.attrValue'), placeholder: '230' },
                        { key: 'unit', label: t('admin.jsonEditor.attrUnit'), placeholder: 'V' },
                        { key: 'group', label: t('admin.jsonEditor.attrGroup'), placeholder: 'Elektrik' },
                      ]}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  </div>

                  <div className="relative group/input">
                    <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
                      {t('admin.productForm.priceScales')}
                    </label>
                    <JsonTableEditor
                      value={formData.price_scales}
                      onChange={(s) => setFormData({ ...formData, price_scales: s })}
                      columns={[
                        { key: 'fromQty', label: t('admin.jsonEditor.scaleQty'), type: 'number', placeholder: '5' },
                        { key: 'price', label: t('admin.jsonEditor.scalePrice'), type: 'number', placeholder: '1180.00' },
                      ]}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  </div>

                  <div className="relative group/input">
                    <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
                      {t('admin.productForm.contractData')}
                    </label>
                    <JsonObjectEditor
                      value={formData.contract_data}
                      onChange={(s) => setFormData({ ...formData, contract_data: s })}
                      fields={[
                        { key: 'exists', label: t('admin.jsonEditor.contractExists'), type: 'checkbox' },
                        { key: 'number', label: t('admin.jsonEditor.contractNumber'), placeholder: '4500012345' },
                        { key: 'item', label: t('admin.jsonEditor.contractItem'), placeholder: '10' },
                        { key: 'type', label: t('admin.jsonEditor.contractType'), placeholder: 'MK' },
                        { key: 'validFrom', label: t('admin.jsonEditor.contractValidFrom'), type: 'date' },
                        { key: 'validTo', label: t('admin.jsonEditor.contractValidTo'), type: 'date' },
                        { key: 'targetQty', label: t('admin.jsonEditor.contractTargetQty'), type: 'number', placeholder: '0' },
                        { key: 'targetValue', label: t('admin.jsonEditor.contractTargetValue'), type: 'number', placeholder: '0' },
                        { key: 'contractPrice', label: t('admin.jsonEditor.contractPrice'), type: 'number', placeholder: '0' },
                      ]}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  </div>

                  <div className="relative group/input">
                    <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
                      {t('admin.productForm.documents')}
                    </label>
                    <JsonTableEditor
                      value={formData.documents}
                      onChange={(s) => setFormData({ ...formData, documents: s })}
                      columns={[
                        { key: 'type', label: t('admin.jsonEditor.docType'), placeholder: 'datasheet' },
                        { key: 'title', label: t('admin.jsonEditor.docTitle'), placeholder: 'Datenblatt' },
                        { key: 'fileFormat', label: t('admin.jsonEditor.docFormat'), placeholder: 'PDF' },
                        { key: 'url', label: t('admin.jsonEditor.docUrl'), placeholder: 'https://...' },
                      ]}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  </div>

                  <div className="relative group/input">
                    <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>
                      {t('admin.productForm.compliance')}
                    </label>
                    <JsonTableEditor
                      value={formData.compliance}
                      onChange={(s) => setFormData({ ...formData, compliance: s })}
                      columns={[
                        { key: 'label', label: t('admin.jsonEditor.compLabel'), placeholder: 'ISO 9001:2015' },
                      ]}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  </div>
                </div>
              </div>
              {/* ===== Ende JSON-Detail-Felder ===== */}

              <div className="relative group/checkbox">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 backdrop-blur-xl border border-white/20 shadow-[0_4px_16px_rgba(0,0,0,0.2)]'
                  : 'bg-slate-50 border border-slate-200'
                } rounded-2xl`}></div>
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10' : ''} rounded-2xl`}></div>

                <div className="relative p-4 flex items-center">
                  <div className="relative">
                    <div className={`absolute inset-0 ${isDarkMode
                      ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl border border-white/30 shadow-[0_4px_16px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]'
                      : ''
                    } rounded-lg`}></div>
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className={`relative h-5 w-5 ${isDarkMode
                        ? 'text-blue-400 focus:ring-blue-500/50 border-0 bg-transparent'
                        : 'text-brand focus:ring-brand/40 border-slate-300 rounded'
                      } focus:ring-2 transition-all duration-300 cursor-pointer`}
                    />
                  </div>
                  <label htmlFor="is_active" className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'} cursor-pointer`}>
                    {t('admin.productForm.isActive')}
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6">
                <div className={`absolute left-0 right-0 h-px bg-gradient-to-r from-transparent ${isDarkMode ? 'via-white/40' : 'via-slate-200'} to-transparent mb-6`}>
                  <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-px bg-gradient-to-r from-blue-400/60 to-blue-400/60 blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                </div>
                
                <button
                  type="button"
                  onClick={onCancel}
                  className={secondaryButtonStyles.className}
                  style={secondaryButtonStyles.style}
                >
                  <div 
                    className={secondaryButtonStyles.hoverOverlay}
                    style={secondaryButtonStyles.hoverStyle}
                  ></div>
                  
                  <div className={`relative z-10 px-6 py-3 font-medium transition-all duration-300 ${
                    isDarkMode ? 'text-white' : 'text-black'
                  } drop-shadow-lg group-hover:drop-shadow-xl`}>
                    {t('common.cancel')}
                  </div>
                  
                  <div className={secondaryButtonStyles.waveEffect}>
                    <div 
                      className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-800"
                      style={{
                        background: isDarkMode
                          ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                          : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)'
                      }}
                    ></div>
                  </div>
                </button>
                
                <button
                  type="submit"
                  className={primaryButtonStyles.className}
                  style={{
                    ...primaryButtonStyles.style,
                    background: isDarkMode 
                      ? (product 
                          ? 'linear-gradient(135deg, rgba(34,197,94,0.7) 0%, rgba(21,128,61,0.7) 50%, rgba(22,101,52,0.7) 100%)'
                          : primaryButtonStyles.style.background)
                      : (product 
                          ? 'linear-gradient(135deg, rgba(34,197,94,0.9) 0%, rgba(21,128,61,0.9) 50%, rgba(22,101,52,0.9) 100%)'
                          : primaryButtonStyles.style.background),
                    boxShadow: isDarkMode 
                      ? (product 
                          ? '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                          : primaryButtonStyles.style.boxShadow)
                      : (product 
                          ? '0 6px 24px rgba(34,197,94,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)'
                          : primaryButtonStyles.style.boxShadow)
                  }}
                >
                  <div 
                    className={primaryButtonStyles.hoverOverlay}
                    style={primaryButtonStyles.hoverStyle}
                  ></div>
                  
                  <div className="relative z-10 px-6 py-3 font-medium text-white transition-all duration-300 drop-shadow-lg group-hover:drop-shadow-xl">
                    {product ? t('common.update') : t('common.create')}
                  </div>
                  
                  <div className={primaryButtonStyles.waveEffect}>
                    <div 
                      className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-800"
                      style={{
                        background: isDarkMode
                          ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                          : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)'
                      }}
                    ></div>
                  </div>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;